import { useState } from 'react'
import { Popover, ArrowContainer } from 'react-tiny-popover'

type HelpPopoverProps = {
  children: React.ReactNode
}

export const HelpPopover: React.FC<HelpPopoverProps> = ({ children }) => {
  const [isPopoverOpen, setIsPopoverOpen] = useState(false)
  return (
    <Popover
      isOpen={isPopoverOpen}
      padding={10}
      onClickOutside={() => setIsPopoverOpen(false)}
      positions={['right', 'top']} // preferred positions by priority
      content={({ position, childRect, popoverRect }) => (
        <ArrowContainer
          position={position}
          childRect={childRect}
          popoverRect={popoverRect}
          arrowColor="#000000"
          arrowSize={6}
          className="help-popover-arrow-container"
        >
          <div className="help-popover-container">{children}</div>
        </ArrowContainer>
      )}
    >
      <button
        className="help-popover-trigger"
        tabIndex={0}
        type="button"
        onClick={() => setIsPopoverOpen(!isPopoverOpen)}
      >
        ?
      </button>
    </Popover>
  )
}

export const WorkAudioUrlHelp: React.FC = () => (
  <HelpPopover>
    This audio URL field will only be used if no audio tracks are configured via the standard upload process. Should only be used for unusual situations.
  </HelpPopover>
)

export const WorkBacklinkHelp: React.FC = () => (
  <HelpPopover>
    If backlink URL is specified, when user clicks back on the caption page,
    they will go to that URL.
    If it is not specified, user will go back to the exhibition index.
  </HelpPopover>
)

export const WorkTitleHelp: React.FC = () => (
  <HelpPopover>
    Required. You may also include and format artist name, date, etc. here.
  </HelpPopover>
)

export const WorkDilationHelp: React.FC = () => (
  <HelpPopover>
    If using an analog media playback device like a 16mm projector or cassette deck,
    the captions may be too fast or slow if they were sourced from a digital copy of the media
    at a different frame rate/plyaback speed.  Use this factor to slow down or speed up captions.
  </HelpPopover>
)

export const WorkDurationHelp: React.FC = () => (
  <HelpPopover>
    <b>
      Work duration will be automatically set by the media player
      when media file is first loaded.
    </b>
    <br />
    Before this point, duration will default to the timestamp
    of the last caption (or one hour if no captions uploaded).
  </HelpPopover>
)

export const WorkAudioUploaderHelper: React.FC = () => (
  <HelpPopover>
    Upload an .mp3 file (195MB max file size) which will be used as a synchronized audio track for this work.  Once uploaded, processing will take 2-5minutes.  Please stay on this page until processing is complete.
  </HelpPopover>
)

export const WorkSRTShortDescriptorHelp: React.FC = () => (
  <HelpPopover>
    The short descriptor should be 2-5 characters representing the language code for this caption track (e.g.
    {' '}
    <em>EN-US</em>
    {' '}
    or
    {' '}
    <em>ES</em>
    )
  </HelpPopover>
)

export const ExhibitionWorkLabelHelper: React.FC = () => (
  <HelpPopover>
    Label to display the count of works in the exhibition.
    Enter the
    {' '}
    <b>singular</b>
    {' '}
    form here (it will be pluralized -- e.g. &quot;Audio Zone&quot; will display as
    {' '}
    <b>1 Audio Zone</b>
    {' '}
    or
    {' '}
    <b>n Audio Zones</b>
    ).
  </HelpPopover>
)

export const AutoTranslationHelper: React.FC = () => (
  <HelpPopover>
    (First caption file above will be used as source, ctrl/command/shift-click to select multiple)
  </HelpPopover>
)

export const OnScreenSubtitlesLanguageHelp: React.FC = () => (
  <HelpPopover>
    Selects which caption track should be shown on the media player's video display when on screen captions are enabled (see right).
    Caption language should change soon after 'Update Media Player' has been pressed, if player is connected to the network and on screen captions are enabled.

    Note: requires AccessKit Player Package version 1.1 or higher.
  </HelpPopover>
)

export const OnScreenSubtitlesActiveHelp: React.FC = () => (
  <HelpPopover>
    Controls whether captions are displayed as subtitles on the media player's video display. For multi-channel works,
    on-screen captions can be toggled for all media players using the work controls.
  </HelpPopover>
)

export const WorkPublishedHelp: React.FC = () => (
  <HelpPopover>
    Published works will be visible in Guest View.
  </HelpPopover>
)

export const ExhibitionPublishedHelp: React.FC = () => (
  <HelpPopover>
    Published exhibitions will be visible in Guest View.
  </HelpPopover>
)

export const ExhibitionStatusHelp: React.FC = () => (
  <HelpPopover>
    Exhibitions are sorted by their Exhibition Status in the Admin Dashboard.
    This does not affect visibility in Guest View.
    To make the exhibition visible to guests, select the
    {' '}
    <b>Published?</b>
    {' '}
    option.
  </HelpPopover>
)
